import { KnowledgeMarkdown } from '@/components/features/KnowledgeBase/KnowledgeMarkdown';
import { Button } from '@/components/ui/shadcn/button';
import { Checkbox } from '@/components/ui/shadcn/checkbox';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import { Switch } from '@/components/ui/shadcn/switch';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import { type PageProps } from '@/types';
import { Head, Link, useForm, usePage } from '@inertiajs/react';
import { ArrowLeft, CalendarDays, Clock, Eye, EyeOff, Globe2, Save, Star, Trash2, Upload, User, X } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { route } from 'ziggy-js';

async function uploadImageFile(file: File): Promise<{ url: string; name: string }> {
    const form = new FormData();
    form.append('image', file);
    const csrfMeta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]');
    const res = await fetch(route('knowledge-base.images.upload'), {
        method: 'POST',
        headers: { 'X-CSRF-TOKEN': csrfMeta?.content ?? '', Accept: 'application/json' },
        body: form,
    });
    if (!res.ok) throw new Error('Upload failed');
    return res.json();
}

interface KnowledgeCategory {
    id: string;
    name: string;
    slug: string;
}

interface KnowledgeArticle {
    id: string;
    category: KnowledgeCategory | null;
    translation_group_uuid: string;
    locale: string;
    title: string;
    slug: string;
    summary: string | null;
    body_markdown: string;
    status: string;
    is_featured: boolean;
    tags: string[];
    visible_to_roles: string[];
    visible_to_permissions: string[];
    review_due_at: string | null;
    author?: { id: string; name: string } | null;
    updated_at?: string | null;
}

interface KnowledgeBaseEditProps extends PageProps {
    article: KnowledgeArticle | null;
    categories: KnowledgeCategory[];
    form_options: {
        roles: string[];
        permissions: string[];
        statuses: string[];
    };
    locales: string[];
    [key: string]: unknown;
}

interface KnowledgeArticleForm {
    [key: string]: string | string[] | boolean;
    category_id: string;
    translation_group_uuid: string;
    locale: string;
    title: string;
    slug: string;
    summary: string;
    body_markdown: string;
    status: string;
    is_featured: boolean;
    tags: string;
    visible_to_roles: string[];
    visible_to_permissions: string[];
    review_due_at: string;
}

function toggleValue(values: string[], value: string): string[] {
    return values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
}

function estimateReadTime(markdown: string): number {
    const wordCount = markdown
        .replace(/```[\s\S]*?```/g, '')
        .replace(/[#*`_[\]()>-]/g, '')
        .trim()
        .split(/\s+/)
        .filter(Boolean).length;
    return Math.max(1, Math.ceil(wordCount / 200));
}

export default function KnowledgeBaseEditPage() {
    const { props } = usePage<KnowledgeBaseEditProps>();
    const { article, categories, form_options: formOptions, locales, auth } = props;
    const authUser = (auth as { user?: { name?: string } })?.user;
    const isEditing = article !== null;
    const { t } = useTranslations();

    const { data, setData, post, put, transform, processing, errors } = useForm<KnowledgeArticleForm>({
        category_id: article?.category?.id ?? '',
        translation_group_uuid: article?.translation_group_uuid ?? '',
        locale: article?.locale ?? locales[0] ?? 'en',
        title: article?.title ?? '',
        slug: article?.slug ?? '',
        summary: article?.summary ?? '',
        body_markdown: article?.body_markdown ?? '# New article\n\nStart writing here.',
        status: article?.status ?? 'draft',
        is_featured: article?.is_featured ?? false,
        tags: article?.tags.join(', ') ?? '',
        visible_to_roles: article?.visible_to_roles ?? [],
        visible_to_permissions: article?.visible_to_permissions ?? [],
        review_due_at: article?.review_due_at ?? '',
    });

    const [showPreview, setShowPreview] = useState(false);
    const [tagInput, setTagInput] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const mdFileRef = useRef<HTMLInputElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const insertImageMarkdown = (url: string, name: string) => {
        const ta = textareaRef.current;
        const alt = name.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' ');
        const tag = `![${alt}](${url})`;
        if (!ta) {
            setData('body_markdown', data.body_markdown + '\n' + tag);
            return;
        }
        const start = ta.selectionStart ?? data.body_markdown.length;
        const end = ta.selectionEnd ?? start;
        const before = data.body_markdown.slice(0, start);
        const after = data.body_markdown.slice(end);
        const needsNewline = before.length > 0 && !before.endsWith('\n');
        const inserted = (needsNewline ? '\n' : '') + tag + '\n';
        const next = before + inserted + after;
        setData('body_markdown', next);
        requestAnimationFrame(() => {
            const pos = start + inserted.length;
            ta.setSelectionRange(pos, pos);
            ta.focus();
        });
    };

    const handleImageFiles = async (files: FileList | File[]) => {
        const images = Array.from(files).filter((f) => f.type.startsWith('image/'));
        if (!images.length) return;
        setUploadingImage(true);
        try {
            for (const img of images) {
                const { url, name } = await uploadImageFile(img);
                insertImageMarkdown(url, name);
            }
        } catch {
            // silently fail — browser console will show fetch error
        } finally {
            setUploadingImage(false);
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLTextAreaElement>) => {
        e.preventDefault();
        setIsDragging(false);
        const files = e.dataTransfer.files;
        if (files.length) void handleImageFiles(files);
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const items = Array.from(e.clipboardData.items).filter((i) => i.kind === 'file' && i.type.startsWith('image/'));
        if (!items.length) return;
        e.preventDefault();
        const files = items.map((i) => i.getAsFile()).filter(Boolean) as File[];
        void handleImageFiles(files);
    };

    const handleMdUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const content = ev.target?.result as string;
            setData('body_markdown', content);
        };
        reader.readAsText(file);
        e.target.value = '';
    };

    const tagList = useMemo(
        () =>
            data.tags
                .split(',')
                .map((t) => t.trim())
                .filter(Boolean),
        [data.tags],
    );
    const readTime = useMemo(() => estimateReadTime(data.body_markdown), [data.body_markdown]);
    const wordCount = useMemo(
        () =>
            data.body_markdown
                .replace(/```[\s\S]*?```/g, '')
                .replace(/[#*`_[\]()>-]/g, '')
                .trim()
                .split(/\s+/)
                .filter(Boolean).length,
        [data.body_markdown],
    );

    const addTag = (raw: string) => {
        const tag = raw.trim();
        if (!tag || tagList.includes(tag)) return;
        const updated = [...tagList, tag];
        setData('tags', updated.join(', '));
        setTagInput('');
    };

    const removeTag = (tag: string) => {
        setData('tags', tagList.filter((t) => t !== tag).join(', '));
    };

    const buildTransform = (statusOverride?: string) =>
        transform((d) => ({
            ...d,
            status: statusOverride ?? d.status,
            is_featured: d.is_featured,
            category_id: d.category_id || null,
            translation_group_uuid: d.translation_group_uuid || null,
            slug: d.slug || null,
            summary: d.summary || null,
            tags: d.tags
                .split(',')
                .map((tag) => tag.trim())
                .filter(Boolean),
            review_due_at: d.review_due_at || null,
        }));

    const submitWith = (statusOverride?: string) => {
        buildTransform(statusOverride);
        if (isEditing) {
            put(route('knowledge-base.update', article.id));
        } else {
            post(route('knowledge-base.store'));
        }
    };

    const breadcrumbs = [
        { title: t('knowledge_base.index.breadcrumbs.knowledge_base', 'Knowledge Base'), href: '/knowledge-base' },
        ...(article?.category ? [{ title: article.category.name, href: `${route('knowledge-base.index')}?category=${article.category.slug}` }] : []),
        {
            title: isEditing ? t('knowledge_base.edit.breadcrumb_edit', 'Edit article') : t('knowledge_base.edit.breadcrumb_create', 'New article'),
            href: isEditing ? route('knowledge-base.edit', article.id) : route('knowledge-base.create'),
        },
    ];

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={isEditing ? t('knowledge_base.edit.title_edit', 'Edit Article') : t('knowledge_base.edit.title_create', 'New Article')} />

            <div className="flex flex-1 flex-col overflow-hidden" style={{ minHeight: 0 }}>
                {/* Page header */}
                <div className="border-b px-6 py-4">
                    {/* Back link */}
                    <Link
                        href={
                            isEditing ? route('knowledge-base.show', { slug: article.slug, locale: article.locale }) : route('knowledge-base.index')
                        }
                        className="text-muted-foreground hover:text-foreground mb-3 inline-flex items-center gap-1.5 text-sm transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        {isEditing
                            ? t('knowledge_base.edit.actions.back_to_article', 'Back to article')
                            : t('knowledge_base.edit.actions.back', 'Articles')}
                    </Link>

                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <h1 className="text-xl font-semibold">
                                {isEditing
                                    ? t('knowledge_base.edit.title_edit', 'Edit article')
                                    : t('knowledge_base.edit.title_create', 'New article')}
                            </h1>
                            <p className="text-muted-foreground mt-0.5 text-sm">
                                {t(
                                    'knowledge_base.edit.subtitle',
                                    'Write in Markdown. Published articles are visible immediately to users with access.',
                                )}
                            </p>
                        </div>

                        {/* Action buttons */}
                        <div className="flex shrink-0 items-center gap-2">
                            <Button type="button" variant="outline" size="sm" onClick={() => setShowPreview((v) => !v)}>
                                {showPreview ? (
                                    <>
                                        <EyeOff className="h-4 w-4" />
                                        {t('knowledge_base.edit.tabs.write', 'Write')}
                                    </>
                                ) : (
                                    <>
                                        <Eye className="h-4 w-4" />
                                        {t('knowledge_base.edit.tabs.preview', 'Preview')}
                                    </>
                                )}
                            </Button>
                            <Button type="button" variant="outline" size="sm" disabled={processing} onClick={() => submitWith('draft')}>
                                <Save className="h-4 w-4" />
                                {t('knowledge_base.edit.actions.save_draft', 'Save draft')}
                            </Button>
                            <Button type="button" size="sm" disabled={processing} onClick={() => submitWith('published')}>
                                {processing
                                    ? t('knowledge_base.edit.actions.saving', 'Saving...')
                                    : t('knowledge_base.edit.actions.publish', 'Publish')}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Form body */}
                <div className="flex flex-1 overflow-hidden">
                    <div className="flex-1 overflow-y-auto px-6 py-6">
                        <div className="mx-auto max-w-3xl space-y-5">
                            {/* Article title */}
                            <div className="space-y-1.5">
                                <Label htmlFor="title" className="text-sm font-medium">
                                    {t('knowledge_base.edit.fields.title', 'Article title')}
                                    <span className="text-destructive ml-0.5">*</span>
                                </Label>
                                <Input
                                    id="title"
                                    value={data.title}
                                    onChange={(e) => setData('title', e.target.value)}
                                    className="h-11 text-base"
                                    placeholder={t('knowledge_base.edit.fields.title_placeholder', 'Enter article title')}
                                />
                                {errors.title && <p className="text-destructive text-sm">{errors.title}</p>}
                            </div>

                            {/* Summary + Category */}
                            <div className="grid gap-4 md:grid-cols-2">
                                <div className="space-y-1.5">
                                    <Label htmlFor="summary" className="text-sm font-medium">
                                        {t('knowledge_base.edit.fields.summary', 'Short description (excerpt)')}
                                    </Label>
                                    <Textarea
                                        id="summary"
                                        value={data.summary}
                                        onChange={(e) => setData('summary', e.target.value)}
                                        rows={4}
                                        placeholder={t('knowledge_base.edit.fields.summary_placeholder', 'Brief description of the article...')}
                                    />
                                    {data.summary && (
                                        <p className="text-muted-foreground text-xs">
                                            {data.summary.length}/160 {t('knowledge_base.edit.fields.characters', 'characters')}
                                        </p>
                                    )}
                                    {errors.summary && <p className="text-destructive text-sm">{errors.summary}</p>}
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-sm font-medium">{t('knowledge_base.edit.fields.category', 'Category')}</Label>
                                    <Select value={data.category_id || 'none'} onValueChange={(v) => setData('category_id', v === 'none' ? '' : v)}>
                                        <SelectTrigger>
                                            <SelectValue placeholder={t('knowledge_base.edit.fields.no_category', 'No category')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">{t('knowledge_base.edit.fields.no_category', 'No category')}</SelectItem>
                                            {categories.map((category) => (
                                                <SelectItem key={category.id} value={category.id}>
                                                    {category.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* Tags */}
                            <div className="space-y-1.5">
                                <Label className="text-sm font-medium">{t('knowledge_base.edit.fields.tags', 'Tags')}</Label>
                                <div className="flex min-h-10 flex-wrap items-center gap-1.5 rounded-md border px-3 py-2">
                                    {tagList.map((tag) => (
                                        <span key={tag} className="bg-muted flex items-center gap-1 rounded px-2 py-0.5 text-sm">
                                            {tag}
                                            <button
                                                type="button"
                                                onClick={() => removeTag(tag)}
                                                className="text-muted-foreground hover:text-foreground ml-0.5"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </span>
                                    ))}
                                    <input
                                        value={tagInput}
                                        onChange={(e) => setTagInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ',') {
                                                e.preventDefault();
                                                if (tagInput.trim()) addTag(tagInput);
                                            } else if (e.key === 'Backspace' && !tagInput && tagList.length > 0) {
                                                removeTag(tagList[tagList.length - 1]);
                                            }
                                        }}
                                        onBlur={() => {
                                            if (tagInput.trim()) addTag(tagInput);
                                        }}
                                        placeholder={
                                            tagList.length === 0 ? t('knowledge_base.edit.fields.tags_placeholder', 'onboarding, hardware') : ''
                                        }
                                        className="min-w-24 flex-1 bg-transparent text-sm outline-none"
                                    />
                                </div>
                                <p className="text-muted-foreground text-xs">
                                    {t('knowledge_base.edit.fields.tags_hint', 'Press Enter or comma to add a tag')}
                                </p>
                            </div>

                            {/* Content */}
                            <div className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                    <Label className="text-sm font-medium">
                                        {t('knowledge_base.edit.fields.content', 'Content')}
                                        <span className="text-destructive ml-0.5">*</span>
                                    </Label>
                                    <button
                                        type="button"
                                        onClick={() => mdFileRef.current?.click()}
                                        className="text-muted-foreground hover:bg-accent hover:text-foreground flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition-colors"
                                    >
                                        <Upload className="h-3.5 w-3.5" />
                                        {t('knowledge_base.edit.actions.upload_md', 'Upload .md')}
                                    </button>
                                    <input
                                        ref={mdFileRef}
                                        type="file"
                                        accept=".md,.markdown,text/markdown"
                                        className="hidden"
                                        onChange={handleMdUpload}
                                    />
                                </div>
                                {showPreview ? (
                                    <div className="bg-card min-h-[520px] rounded-xl border p-6">
                                        <KnowledgeMarkdown>{data.body_markdown}</KnowledgeMarkdown>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <Textarea
                                            ref={textareaRef}
                                            value={data.body_markdown}
                                            onChange={(e) => setData('body_markdown', e.target.value)}
                                            onDrop={handleDrop}
                                            onPaste={handlePaste}
                                            onDragOver={(e) => {
                                                e.preventDefault();
                                                setIsDragging(true);
                                            }}
                                            onDragLeave={() => setIsDragging(false)}
                                            className={`min-h-[520px] font-mono text-sm transition-colors ${isDragging ? 'border-primary bg-primary/5' : ''}`}
                                        />
                                        {(isDragging || uploadingImage) && (
                                            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-md">
                                                {uploadingImage ? (
                                                    <span className="bg-background/90 text-muted-foreground rounded-md px-3 py-1.5 text-sm">
                                                        {t('knowledge_base.edit.actions.uploading_image', 'Uploading…')}
                                                    </span>
                                                ) : (
                                                    <span className="bg-background/90 text-primary border-primary/30 rounded-md border px-3 py-1.5 text-sm font-medium">
                                                        {t('knowledge_base.edit.actions.drop_image', 'Drop image to upload')}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {errors.body_markdown && <p className="text-destructive mt-1 text-sm">{errors.body_markdown}</p>}
                                <div className="text-muted-foreground flex items-center gap-3 text-xs">
                                    <span>
                                        {wordCount} {t('knowledge_base.edit.stats.words', 'words')}
                                    </span>
                                    <span>·</span>
                                    <span>
                                        {data.body_markdown.length} {t('knowledge_base.edit.stats.characters', 'characters')}
                                    </span>
                                    <span>·</span>
                                    <span>
                                        {readTime} {t('knowledge_base.edit.stats.min_read', 'min read')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right sidebar */}
                    <aside className="hidden w-72 shrink-0 flex-col overflow-y-auto border-l lg:flex">
                        <div className="space-y-0 divide-y">
                            {/* Publishing */}
                            <div className="space-y-3 p-4">
                                <h2 className="text-sm font-semibold">{t('knowledge_base.edit.sections.publishing', 'Publishing')}</h2>
                                <div className="space-y-1.5">
                                    <Label className="text-muted-foreground text-xs">{t('knowledge_base.edit.fields.status', 'Status')}</Label>
                                    <Select value={data.status} onValueChange={(v) => setData('status', v)}>
                                        <SelectTrigger className="h-8 text-sm">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {formOptions.statuses.map((status) => (
                                                <SelectItem key={status} value={status}>
                                                    {t(`knowledge_base.status.${status}`, status)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {data.status === 'draft' && (
                                        <p className="text-muted-foreground text-xs">
                                            {t('knowledge_base.edit.fields.status_draft_hint', 'Not visible to readers until published.')}
                                        </p>
                                    )}
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-muted-foreground text-xs">{t('knowledge_base.edit.fields.language', 'Language')}</Label>
                                    <Select value={data.locale} onValueChange={(v) => setData('locale', v)}>
                                        <SelectTrigger className="h-8 text-sm">
                                            <Globe2 className="h-3.5 w-3.5" />
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {locales.map((locale) => (
                                                <SelectItem key={locale} value={locale}>
                                                    {locale.toUpperCase()}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* Article details */}
                            <div className="space-y-3 p-4">
                                <h2 className="text-sm font-semibold">{t('knowledge_base.edit.sections.article_details', 'Article details')}</h2>
                                <div className="space-y-2.5 text-sm">
                                    {(isEditing ? article?.author?.name : authUser?.name) && (
                                        <div className="flex items-center justify-between">
                                            <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                                <User className="h-3.5 w-3.5" />
                                                {t('knowledge_base.edit.fields.author', 'Author')}
                                            </span>
                                            <span className="text-sm font-medium">{isEditing ? article?.author?.name : authUser?.name}</span>
                                        </div>
                                    )}
                                    {isEditing && article?.updated_at && (
                                        <div className="flex items-center justify-between">
                                            <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                                <CalendarDays className="h-3.5 w-3.5" />
                                                {t('knowledge_base.edit.fields.last_updated', 'Last updated')}
                                            </span>
                                            <span className="text-xs">
                                                {new Date(article.updated_at).toLocaleDateString('en', {
                                                    month: 'short',
                                                    day: 'numeric',
                                                    year: 'numeric',
                                                })}
                                            </span>
                                        </div>
                                    )}
                                    <div className="flex items-center justify-between">
                                        <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                            <Clock className="h-3.5 w-3.5" />
                                            {t('knowledge_base.edit.fields.read_time', 'Est. read time')}
                                        </span>
                                        <span className="text-xs">
                                            {readTime} {t('knowledge_base.edit.stats.min_read', 'min')}
                                        </span>
                                    </div>

                                    <div className="flex items-center justify-between pt-1">
                                        <Label
                                            htmlFor="is_featured"
                                            className="text-muted-foreground flex cursor-pointer items-center gap-1.5 text-xs"
                                        >
                                            <Star className="h-3.5 w-3.5" />
                                            {t('knowledge_base.edit.fields.is_featured', 'Featured article')}
                                        </Label>
                                        <Switch
                                            id="is_featured"
                                            checked={data.is_featured as boolean}
                                            onCheckedChange={(v) => setData('is_featured', v)}
                                        />
                                    </div>
                                    {data.is_featured && (
                                        <p className="text-muted-foreground text-xs">
                                            {t(
                                                'knowledge_base.edit.fields.is_featured_hint',
                                                'Shown in the featured banner on the Knowledge Base homepage. Only one article per language can be featured.',
                                            )}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Visibility */}
                            <div className="space-y-3 p-4">
                                <h2 className="text-sm font-semibold">{t('knowledge_base.edit.sections.visibility', 'Visibility')}</h2>
                                <p className="text-muted-foreground text-xs">
                                    {t(
                                        'knowledge_base.edit.sections.visibility_hint',
                                        'Leave empty to make the article visible to every authenticated user.',
                                    )}
                                </p>
                                <div className="space-y-1.5">
                                    <Label className="text-muted-foreground text-xs">{t('knowledge_base.edit.sections.roles', 'Roles')}</Label>
                                    <div className="max-h-36 space-y-1.5 overflow-y-auto rounded-md border p-2">
                                        {formOptions.roles.length === 0 && (
                                            <p className="text-muted-foreground text-xs">
                                                {t('knowledge_base.edit.sections.no_roles', 'No roles yet.')}
                                            </p>
                                        )}
                                        {formOptions.roles.map((role) => (
                                            <label key={role} className="flex cursor-pointer items-center gap-2 text-sm">
                                                <Checkbox
                                                    checked={data.visible_to_roles.includes(role)}
                                                    onCheckedChange={() => setData('visible_to_roles', toggleValue(data.visible_to_roles, role))}
                                                />
                                                {role}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-muted-foreground text-xs">
                                        {t('knowledge_base.edit.sections.permissions', 'Permissions')}
                                    </Label>
                                    <div className="max-h-40 space-y-1.5 overflow-y-auto rounded-md border p-2">
                                        {formOptions.permissions.map((permission) => (
                                            <label key={permission} className="flex cursor-pointer items-center gap-2 text-sm">
                                                <Checkbox
                                                    checked={data.visible_to_permissions.includes(permission)}
                                                    onCheckedChange={() =>
                                                        setData('visible_to_permissions', toggleValue(data.visible_to_permissions, permission))
                                                    }
                                                />
                                                <span className="truncate">{permission}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* SEO & URL */}
                            <div className="space-y-3 p-4">
                                <h2 className="text-sm font-semibold">{t('knowledge_base.edit.sections.seo_url', 'SEO & URL')}</h2>
                                <div className="space-y-1.5">
                                    <Label htmlFor="slug" className="text-muted-foreground text-xs">
                                        {t('knowledge_base.edit.fields.slug', 'Slug')}
                                    </Label>
                                    <Input
                                        id="slug"
                                        value={data.slug}
                                        onChange={(e) => setData('slug', e.target.value)}
                                        placeholder={t('knowledge_base.edit.fields.slug_hint', 'Generated from title if empty')}
                                        className="h-8 text-sm"
                                    />
                                    {errors.slug && <p className="text-destructive text-xs">{errors.slug}</p>}
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="review_due_at" className="text-muted-foreground text-xs">
                                        {t('knowledge_base.edit.fields.review_due', 'Review due')}
                                    </Label>
                                    <Input
                                        id="review_due_at"
                                        type="date"
                                        value={data.review_due_at}
                                        onChange={(e) => setData('review_due_at', e.target.value)}
                                        className="h-8 text-sm"
                                    />
                                </div>
                            </div>

                            {/* Archive */}
                            {isEditing && article.status !== 'archived' && (
                                <div className="p-4">
                                    <button
                                        type="button"
                                        disabled={processing}
                                        onClick={() => {
                                            if (
                                                confirm(
                                                    t(
                                                        'knowledge_base.edit.actions.archive_confirm',
                                                        'Archive this article? It can be restored later.',
                                                    ),
                                                )
                                            ) {
                                                submitWith('archived');
                                            }
                                        }}
                                        className="text-destructive hover:bg-destructive/10 border-destructive/30 flex w-full items-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                        {t('knowledge_base.edit.actions.archive', 'Archive article')}
                                    </button>
                                    <p className="text-muted-foreground mt-1.5 text-xs">
                                        {t('knowledge_base.edit.actions.archive_hint', 'Move this article to the archive. It can be restored later.')}
                                    </p>
                                </div>
                            )}
                        </div>
                    </aside>
                </div>
            </div>
        </AppLayout>
    );
}
